import api from "./api";

let _remoteAudio = null;

// Coda delle letture: ogni chiamata a speak() si accoda invece di sovrapporsi o
// interrompere quella in corso — una lettura parte solo quando la precedente è
// davvero finita (onEnd), mai in parallelo.
let _queue = [];
let _speakingJob = false;

export function cancelSpeech() {
  _queue = [];
  _speakingJob = false;
  if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
  if (_remoteAudio) {
    try {
      _remoteAudio.pause();
    } catch {
      /* ignore */
    }
    _remoteAudio = null;
  }
}

function pickVoice(lang, name) {
  const vs = window.speechSynthesis?.getVoices?.() || [];
  if (name) {
    const byName = vs.find((v) => v.name === name);
    if (byName) return byName;
  }
  return (
    vs.find((v) => v.lang === lang) ||
    vs.find((v) => v.lang && v.lang.startsWith((lang || "it").slice(0, 2)))
  );
}

let _voicesReady = null;
// Chrome (e alcuni WebView) restituisce getVoices() vuoto finché l'evento "voiceschanged"
// non è sparato una prima volta: speak() chiamato prima d'allora spesso fallisce in
// silenzio (nessun suono, nessun errore). Aspettiamo le voci una volta sola (con un
// timeout di sicurezza, per non restare bloccati sui browser che l'evento non lo sparano).
function waitForVoices() {
  if (typeof window === "undefined" || !window.speechSynthesis) return Promise.resolve();
  if (window.speechSynthesis.getVoices().length > 0) return Promise.resolve();
  if (_voicesReady) return _voicesReady;
  _voicesReady = new Promise((resolve) => {
    const done = () => resolve();
    window.speechSynthesis.addEventListener("voiceschanged", done, { once: true });
    setTimeout(done, 1000);
  });
  return _voicesReady;
}

// Speak one or more texts sequentially using the Web Speech API.
// A watchdog guarantees onEnd always fires (even without available voices / headless).
function speakSequenceNow(texts, { lang = "it-IT", voiceName, onStart, onEnd } = {}) {
  const list = (Array.isArray(texts) ? texts : [texts]).filter((t) => t && String(t).trim());

  let finished = false;
  const finishAll = () => {
    if (finished) return;
    finished = true;
    onEnd && onEnd();
  };

  if (typeof window === "undefined" || !window.speechSynthesis || list.length === 0) {
    onStart && onStart();
    finishAll();
    return;
  }

  onStart && onStart();

  waitForVoices().then(() => {
    if (finished) return; // cancelSpeech() chiamato mentre aspettavamo le voci
    window.speechSynthesis.cancel();

    const totalChars = list.reduce((n, t) => n + String(t).length, 0);
    const watchdogMs = Math.min(20000, Math.max(2500, totalChars * 90));
    const watchdog = setTimeout(finishAll, watchdogMs);

    let done = 0;
    const step = () => {
      done += 1;
      if (done >= list.length) {
        clearTimeout(watchdog);
        finishAll();
      }
    };

    list.forEach((text) => {
      const u = new SpeechSynthesisUtterance(String(text));
      u.lang = lang;
      const v = pickVoice(lang, voiceName);
      if (v) u.voice = v;
      u.rate = 1;
      u.pitch = 1;
      u.onend = step;
      u.onerror = step;
      window.speechSynthesis.speak(u);
    });
  });
}

// Chiede al backend l'audio sintetizzato dal provider cloud configurato per il progetto
// (tutti i testi uniti in un'unica richiesta/riproduzione). Torna il Blob audio, o
// null se nessun provider è configurato/utilizzabile per quel progetto (204 o errore) —
// il chiamante ricade sempre sul motore TTS del browser in quel caso.
async function fetchRemoteAudio(projectId, texts, lang) {
  const list = (Array.isArray(texts) ? texts : [texts]).filter((t) => t && String(t).trim());
  if (!list.length) return null;
  try {
    const { data, status } = await api.post(
      `/api/projects/public/${projectId}/tts`,
      { text: list.map((t) => String(t).trim()).join(". "), lang },
      { responseType: "blob", validateStatus: (s) => s === 200 || s === 204 }
    );
    if (status !== 200 || !data || !data.size) return null;
    return data;
  } catch {
    return null;
  }
}

// Sintesi vocale: se useRemote+projectId prova prima il provider cloud configurato
// per il progetto (backend/speech_providers.py); se non disponibile/fallisce ricade
// sempre sulla sintesi del browser (speakSequenceNow).
async function speakNow(texts, { lang = "it-IT", voiceName, useRemote, projectId, onStart, onEnd } = {}) {
  if (useRemote && projectId) {
    const blob = await fetchRemoteAudio(projectId, texts, lang);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      _remoteAudio = audio;
      const cleanup = () => {
        URL.revokeObjectURL(url);
        if (_remoteAudio === audio) _remoteAudio = null;
        onEnd && onEnd();
      };
      onStart && onStart();
      audio.onended = cleanup;
      audio.onerror = cleanup;
      audio.play().catch(cleanup);
      return;
    }
  }
  speakSequenceNow(texts, { lang, voiceName, onStart, onEnd });
}

// Pesca il prossimo job in coda e lo riproduce, ma solo se non ne è già attivo uno:
// ogni job passa il proprio onEnd "avvolto" così l'avvio del successivo è agganciato
// alla vera fine del precedente, non alla chiamata di speak().
function pumpQueue() {
  if (_speakingJob) return;
  const job = _queue.shift();
  if (!job) return;
  _speakingJob = true;
  const { texts, opts } = job;
  const onEnd = () => {
    _speakingJob = false;
    opts.onEnd && opts.onEnd();
    pumpQueue();
  };
  speakNow(texts, { ...opts, onEnd });
}

// Stessa firma/comportamento pubblico di prima, ma ora ogni chiamata si accoda invece
// di sovrapporsi (audio cloud) o interrompere (motore browser) una lettura in corso.
export function speak(texts, opts = {}) {
  _queue.push({ texts, opts });
  pumpQueue();
}

// Web Speech API speech-to-text. Returns the recognition instance (or null if unsupported).
export function startRecognition(lang, { onResult, onEnd } = {}) {
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) {
    onEnd && onEnd("unsupported");
    return null;
  }
  const rec = new SR();
  rec.lang = lang || "it-IT";
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.onresult = (e) => {
    const t = e.results?.[0]?.[0]?.transcript || "";
    onResult && onResult(t);
  };
  rec.onerror = () => onEnd && onEnd("error");
  rec.onend = () => onEnd && onEnd();
  try {
    rec.start();
  } catch {
    onEnd && onEnd("error");
    return null;
  }
  return rec;
}

// Trascrizione via il provider cloud configurato per il progetto: registra il microfono
// (push-to-talk — a differenza del riconoscimento continuo del browser, qui la
// registrazione si ferma solo quando viene chiamato .stop(), stessa interfaccia
// {stop()} di startRecognition così il chiamante (ProjectKiosk.js toggleMic) non deve
// saperne la differenza). Torna null se getUserMedia/MediaRecorder non sono disponibili.
export function startRemoteRecording(projectId, lang, { onResult, onEnd } = {}) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
    onEnd && onEnd("unsupported");
    return null;
  }
  let stopped = false;
  let recorder = null;
  let stream = null;
  const chunks = [];

  const finish = async () => {
    if (stopped) return;
    stopped = true;
    stream?.getTracks().forEach((t) => t.stop());
    if (!chunks.length) {
      onEnd && onEnd();
      return;
    }
    try {
      const blob = new Blob(chunks, { type: recorder?.mimeType || "audio/webm" });
      const fd = new FormData();
      fd.append("file", blob, "audio.webm");
      fd.append("lang", lang || "it-IT");
      const { data, status } = await api.post(`/api/projects/public/${projectId}/stt`, fd, {
        validateStatus: (s) => s === 200 || s === 204,
      });
      if (status === 200 && data?.text) onResult && onResult(data.text);
    } catch {
      /* nessuna trascrizione disponibile: l'utente può semplicemente riprovare */
    } finally {
      onEnd && onEnd();
    }
  };

  navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((s) => {
      if (stopped) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream = s;
      recorder = new MediaRecorder(s);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) chunks.push(e.data);
      };
      recorder.onstop = finish;
      recorder.start();
    })
    .catch(() => {
      stopped = true;
      onEnd && onEnd("error");
    });

  return {
    stop: () => {
      if (recorder && recorder.state !== "inactive") recorder.stop();
      else finish();
    },
  };
}

